<template>
    <svg
        xmlns="http://www.w3.org/2000/svg"
        class="map"
        :width="width"
        :height="height"
        overflow="hidden"
    >
        <g
            ref="group"
            style="transition: all 1s ease-in-out;"
        >
            <path
                :d="comparePath.d"
                :stroke-width="1 / transform.scale"
                class="compare"
            />
            <path
                :d="highlightPath.d"
                :stroke-width="1 / transform.scale"
                class="highlight"
            />

            <g
                v-for="{label, labelPosition, x, y, dy, labelXOffset, labelYOffset} in pointsOfInterest"
                :key="label"
                :class="`point-of-interest ${labelPosition}`"
            >
                <circle
                    :cx="x"
                    :cy="y"
                    :r="4 / transform.scale"
                    class="point-of-interest"
                />
                <!--
                    IE will make your test disappear if the font size is too small (<= ~0.009em)
                    Using calc(0.009 * 1em) converts it to pixels first, which lowers the threshold
                    before IE decides not to render the text
                -->
                <text
                    :x="x + labelXOffset"
                    :y="y + labelYOffset"
                    :dy="dy"
                    class="point-of-interest"
                    :style="`font-size: calc(${(1 / transform.scale).toFixed(6)} * 1em);`"
                >{{ label }}</text>
            </g>

        </g>
    </svg>
</template>

<script>
import MapModel from '@/model/MapModel';

const { geoTransverseMercator, geoPath } = d3;

export default {
    props: {
        model: {
            type: MapModel,
            required: true
        },
        width: {
            type: Number,
            default: 100
        },
        height: {
            type: Number,
            default: 200
        }
    },
    computed: {
        compareMapGeojson() {
            return this.model.compareMapGeojson;
        },
        highlightMapGeojson() {
            return this.model.highlightMapGeojson;
        },

        projection() {
            const projection = geoTransverseMercator()
                .rotate([115, 0])
                .scale(1)
                .translate([0, 0]);

            if (!this.compareMapGeojson) {
                return projection;
            }

            const path = geoPath().projection(projection);

            // const map = this.highlightMapGeojson.features.length > 0 ? this.highlightMapGeojson : this.compareMapGeojson;
            const map = this.compareMapGeojson;
            const scaleFactor = 0.9;

            const bounds = path.bounds(map);
            const scale = scaleFactor / Math.max((bounds[1][0] - bounds[0][0]) / this.width, (bounds[1][1] - bounds[0][1]) / this.height);
            const translate = [(this.width - scale * (bounds[1][0] + bounds[0][0])) / 2, (this.height - scale * (bounds[1][1] + bounds[0][1])) / 2];

            return projection.scale(scale).translate(translate);
        },

        transform() {
            const path = geoPath().projection(this.projection);
            const map = this.highlightMapGeojson;
            const scaleFactor = 0.9;

            const bounds = path.bounds(map);

            const scale = scaleFactor / Math.max((bounds[1][0] - bounds[0][0]) / this.width, (bounds[1][1] - bounds[0][1]) / this.height);
            const [[l, t], [r, b]] = bounds;
            const w = r - l;
            const h = b - t;

            const translate = [
                (0 - (l + w / 2)) * scale + this.width / 2,
                (0 - (t + h / 2)) * scale + this.height / 2
            ];

            // eslint-disable-next-line no-restricted-globals
            if (isNaN(scale) || isNaN(translate[0]) || isNaN(translate[1])) {
                return {
                    str: '',
                    scale: 1,
                    translate: [0, 0]
                };
            }

            return {
                str: `translate(${translate[0]}, ${translate[1]}) scale(${scale})`,
                scale,
                translate
            };
        },

        comparePath() {
            if (!this.compareMapGeojson) {
                return [];
            }

            const path = geoPath().projection(this.projection);
            const [x, y] = path.centroid(this.compareMapGeojson);

            return {
                x,
                y,
                d: path(this.compareMapGeojson),
                label: this.compareMapGeojson.features[0].properties.label
            };
        },

        highlightPath() {
            if (!this.highlightMapGeojson) {
                return [];
            }

            const path = geoPath().projection(this.projection);
            const [x, y] = path.centroid(this.highlightMapGeojson);

            return {
                x,
                y,
                d: path(this.highlightMapGeojson),
                label: this.highlightMapGeojson.features[0].properties.label
            };
        },

        pointsOfInterest() {
            const labelOffsets = {
                left: [-7, 0, '0.3em'],
                right: [7, 0, '0.3em'],
                top: [0, -7, '0'],
                bottom: [0, 7, '0.7em']
            };
            return this.model.pointsOfInterest.map(({ label, labelPosition = 'top', lng, lat }) => {
                const [x, y] = this.projection([lng, lat]);
                const [labelXOffset, labelYOffset, dy] = labelOffsets[labelPosition] || [0, 0, '0em'];

                return {
                    x,
                    y,
                    dy,
                    labelXOffset: labelXOffset / this.transform.scale,
                    labelYOffset: labelYOffset / this.transform.scale,
                    label,
                    labelPosition
                };
            });
        }
    },
    watch: {
        transform(value, oldValue) {
            // eslint-disable-next-line no-restricted-globals
            if (!oldValue || isNaN(oldValue.translate[0])) {
                this.$refs.group.setAttribute('transform', value.str);
                return;
            }
            if (value.str === oldValue.str) {
                this.$refs.group.setAttribute('transform', value.str);
                return;
            }
            const svgGroup = this.$refs.group;
            svgGroup.setAttribute('transform', 'translate(0, 0) scale(1)');
            setTimeout(() => {
                svgGroup.setAttribute('transform', value.str);
            }, 1500);
        }
    },
    mounted() {
        this.$refs.group.setAttribute('transform', this.transform.str);
    }
};
</script>

<style lang="postcss" scoped>

.map {
    @apply
        mx-auto;
    text-rendering:auto;
}

path.compare {
    @apply stroke-current text-primary-4;
    fill:none;
}
path.highlight {
    @apply fill-current stroke-current text-primary-4;
    fill-opacity: 0.5;
}

.point-of-interest {
    @apply text-2xs;
}

.point-of-interest circle {
    @apply fill-current text-neutral-text;
}

.point-of-interest text {
    @apply fill-current text-neutral-text;
}

.point-of-interest.top text {
    text-anchor: middle;
}

.point-of-interest.bottom text {
    text-anchor: middle;
}

.point-of-interest.left text {
    text-anchor: end;
}

.point-of-interest.right text {
    text-anchor: start;
}

.point-of-interest.hide {
    display: none;
}

</style>
